import { optimizeImage } from './fotobook_image_processor.js';

export default class FotobookPhotoSelector {
  constructor() {
    console.log('FotobookPhotoSelector: Constructor called');
    this.selectedFiles = [];
    this.maxFiles =
      window.TOTAL_PHOTOS ||
      ((window.FOTOBOOK_CONFIG?.totalSheets || 5) * 4) ||
      20;
    this.concurrencyLimit = 3; // Límite de subidas/procesos simultáneos
    this.activeUploads = 0;
    this.uploadQueue = [];
    
    // Inicializar Worker para procesamiento fuera del hilo principal
    this.initWorker();
    
    console.log(`FotobookPhotoSelector: Max files (pages) set to ${this.maxFiles}`);
    this.init();
  }

  initWorker() {
    try {
      // Verificar soporte de Worker y OffscreenCanvas
      if (window.Worker && window.OffscreenCanvas) {
        // En Vite/Rails, esta es la forma estándar de cargar un Worker
        this.worker = new Worker(new URL('./image_worker.js', import.meta.url), { type: 'module' });
        this.workerCallbacks = new Map();
        
        this.worker.onmessage = (e) => {
          const { success, blob, width, height, id, error } = e.data;
          const callback = this.workerCallbacks.get(id);
          if (callback) {
            if (success) callback.resolve({ blob, width, height });
            else callback.reject(new Error(error));
            this.workerCallbacks.delete(id);
          }
        };

        this.worker.onerror = (err) => {
          console.error('FotobookPhotoSelector Worker Error:', err);
          this.worker = null; // Fallback a hilo principal en caso de error crítico
        };
      }
    } catch (err) {
      console.warn('FotobookPhotoSelector: Web Worker no disponible, usando hilo principal.', err);
      this.worker = null;
    }
  }

  async optimizeImageWithWorker(file, options) {
    if (!this.worker) {
      // Fallback al hilo principal si el worker no está disponible
      return optimizeImage(file, options);
    }

    const id = Math.random().toString(36).substring(2, 11);
    return new Promise((resolve, reject) => {
      this.workerCallbacks.set(id, { resolve, reject });
      this.worker.postMessage({
        file,
        maxDimension: options.maxDimension || 2500,
        quality: options.quality || 0.8,
        id
      });
      
      // Timeout de seguridad por si el worker se bloquea
      setTimeout(() => {
        if (this.workerCallbacks.has(id)) {
          this.workerCallbacks.delete(id);
          reject(new Error('Timeout de procesamiento en Worker'));
        }
      }, 30000);
    });
  }

  init() {
    this.initializeFileInput();
    this.updateFileCounter();
  }

  initializeFileInput() {
    const fileInput = document.querySelector('#photos-input') || document.querySelector('.input-files');
    if (!fileInput) return;

    // Guardar referencia para poder resetear el valor tras procesar
    this.fileInput = fileInput;

    // Priorizar el máximo definido en el input para mantener consistencia con la vista.
    const maxFromDataset = parseInt(fileInput.dataset.maxFiles, 10);
    if (!Number.isNaN(maxFromDataset) && maxFromDataset > 0) {
      this.maxFiles = maxFromDataset;
    }

    // Configurar el input de archivos
    fileInput.addEventListener('change', (e) => {
      this.handleFileSelection(e.target.files);
    });

    // Configurar el atributo accept
    fileInput.setAttribute('accept', 'image/*');
    fileInput.setAttribute('multiple', 'true');
  }

  handleFileSelection(files) {
    const validFiles = Array.from(files).filter(file => {
      return file.type.startsWith('image/');
    });

    // Calcular espacio disponible
    const remainingSlots = this.maxFiles - this.selectedFiles.length;
    if (remainingSlots <= 0) {
      this.showAlert(`Ya alcanzaste el máximo de ${this.maxFiles} fotos`);
      if (this.fileInput) this.fileInput.value = '';
      return;
    }

    // Tomar solo los que caben
    const filesToAdd = validFiles.slice(0, remainingSlots);
    if (validFiles.length > remainingSlots) {
      this.showAlert(`Solo se agregaron ${remainingSlots} foto(s). Límite: ${this.maxFiles}`);
    }

    // Agregar archivos con estado 'uploading'
    filesToAdd.forEach(file => {
      const photoObj = {
        id: null,
        status: 'uploading',
        file: file,
        localUrl: URL.createObjectURL(file),
        thumbUrl: null,
        url: null
      };
      this.selectedFiles.push(photoObj);
      this.enqueueUpload(photoObj);
    });

    this.updateFileCounter();
    this.updateFileList();
    this.checkPrintButton();

    if (this.fileInput) this.fileInput.value = '';
  }

  enqueueUpload(photoObj) {
    this.uploadQueue.push(photoObj);
    this.processQueue();
  }

  async processQueue() {
    if (this.activeUploads >= this.concurrencyLimit || this.uploadQueue.length === 0) {
      return;
    }

    this.activeUploads++;
    const photoObj = this.uploadQueue.shift();

    try {
      // 1. Optimizar cliente (convertir a JPG y redimensionar) usando Worker
      const optimized = await this.optimizeImageWithWorker(photoObj.file, { maxDimension: 2500, quality: 0.8 });
      
      // 2. Subir al servidor
      await this.uploadFile(photoObj, optimized.blob);
    } catch (error) {
      console.error('Processing/Upload failed:', error);
      photoObj.status = 'error';
      this.showAlert('Error al procesar/subir una foto');
    } finally {
      this.activeUploads--;
      this.updateFileList();
      this.checkPrintButton();
      // Continuar con la cola
      this.processQueue();
    }
  }

  async uploadFile(photoObj, blob) {
    const formData = new FormData();
    // Usar el blob optimizado, o el original si falló la optimización (aunque optimizeImage debería resolver siempre)
    formData.append('file', blob || photoObj.file, photoObj.file.name.replace(/\.[^/.]+$/, "") + ".jpg");

    try {
      const response = await fetch('/photos/upload', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: formData
      });

      if (!response.ok) throw new Error('Error en el servidor');

      const data = await response.json();
      photoObj.id = data.id;
      photoObj.status = 'ready';
      photoObj.thumbUrl = data.thumb_url;
      photoObj.url = data.url;
    } catch (error) {
      throw error; // Capturado por processQueue
    }
  }

  updateFileCounter() {
    const counter = document.querySelector('#photos-selected-counter');
    if (counter) {
      counter.textContent = `${this.selectedFiles.length}`;
    }
  }

  updateFileList() {
    const fileList = document.querySelector('#selected-files-list');
    if (!fileList) return;

    fileList.innerHTML = '';
    fileList.classList.add('grid', 'grid-cols-3', 'gap-2');

    const usedIndices = window.fotobookEditor && window.fotobookEditor.getUsedFileIndices 
      ? window.fotobookEditor.getUsedFileIndices() 
      : [];

    this.selectedFiles.forEach((photo, index) => {
      const isUsed = usedIndices.includes(index);
      const fileItem = document.createElement('div');
      fileItem.className = `relative overflow-hidden rounded bg-gray-100 flex items-center justify-center transition-all duration-300 ${isUsed ? 'ring-2 ring-green-500 ring-offset-1 opacity-80' : 'hover:ring-2 hover:ring-petrol-300'}`;
      fileItem.style.aspectRatio = '1 / 1';

      if (photo.status === 'uploading') {
        fileItem.innerHTML = `
          <div class="flex flex-col items-center justify-center text-gray-500">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-petrol-500 mb-1"></div>
            <span class="text-[10px]">Cargando...</span>
          </div>
          <button type="button" class="absolute top-1 right-1 text-white bg-red-500/80 hover:bg-red-600 rounded-full w-5 h-5 flex items-center justify-center" onclick="window.fotobookPhotoSelector.removeFile(${index})" aria-label="Eliminar">
            <i class="fas fa-times text-[10px]"></i>
          </button>
        `;
      } else if (photo.status === 'ready') {
        fileItem.innerHTML = `
          <img src="${photo.thumbUrl || photo.localUrl}" alt="Preview" class="cursor-pointer w-full h-full block object-cover" draggable="true" data-file-index="${index}" />
          ${isUsed ? '<div class="absolute inset-0 bg-green-500/20 flex items-center justify-center pointer-events-none"><i class="fas fa-check-circle text-white text-xl drop-shadow-md"></i></div>' : ''}
          <button type="button" class="absolute top-0.5 right-0.5 text-white bg-red-500/80 hover:bg-red-600 rounded-full w-4 h-4 flex items-center justify-center z-10" onclick="event.stopPropagation(); window.fotobookPhotoSelector.removeFile(${index})" aria-label="Eliminar">
            <i class="fas fa-times text-[8px]"></i>
          </button>
        `;

        const imgEl = fileItem.querySelector('img');
        if (imgEl) {
          // Click para agregar al primer slot vacío
          imgEl.addEventListener('click', () => {
            if (window.fotobookEditor && window.fotobookEditor.addPhotoToFirstEmptySlot) {
              window.fotobookEditor.addPhotoToFirstEmptySlot(index);
            }
          });

          // Drag para colocar en slot específico
          imgEl.addEventListener('dragstart', (ev) => {
            try {
              ev.dataTransfer.setData('text/fotobook-file-index', String(index));
              ev.dataTransfer.effectAllowed = 'copy';
            } catch (_) {}
          });
        }
      } 
else if (photo.status === 'error') {
        fileItem.innerHTML = `
          <div class="flex flex-col items-center justify-center text-red-500 text-center p-1">
            <i class="fas fa-exclamation-triangle mb-1"></i>
            <span class="text-[10px]">Error</span>
          </div>
          <button type="button" class="absolute top-1 right-1 text-white bg-red-500/80 hover:bg-red-600 rounded-full w-5 h-5 flex items-center justify-center" onclick="window.fotobookPhotoSelector.removeFile(${index})" aria-label="Eliminar">
            <i class="fas fa-times text-[10px]"></i>
          </button>
        `;
      }

      fileList.appendChild(fileItem);
    });
  }

  removeFile(index) {
    const [removed] = this.selectedFiles.splice(index, 1);
    if (removed?.localUrl) {
      URL.revokeObjectURL(removed.localUrl);
    }
    this.updateFileCounter();
    this.updateFileList();
    this.checkPrintButton();
  }

  checkPrintButton() {
    const printButton = document.querySelector('#send-to-print-btn');
    if (!printButton) return;

    const readyCount = this.selectedFiles.filter(p => p.status === 'ready').length;
    
    if (readyCount === this.maxFiles) {
      printButton.classList.remove('disabled');
      printButton.disabled = false;
    } else {
      printButton.classList.add('disabled');
      printButton.disabled = true;
    }
  }

  showAlert(message) {
    // Crear un alert temporal
    const alert = document.createElement('div');
    alert.className = 'fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded shadow-lg z-50';
    alert.textContent = message;
    
    document.body.appendChild(alert);
    
    setTimeout(() => {
      alert.remove();
    }, 3000);
  }

  getSelectedFiles() {
    return this.selectedFiles;
  }

  clearFiles() {
    this.selectedFiles.forEach((photo) => {
      if (photo?.localUrl) {
        URL.revokeObjectURL(photo.localUrl);
      }
    });
    this.selectedFiles = [];
    this.updateFileCounter();
    this.updateFileList();
    this.checkPrintButton();
  }
} 